import { ArchiveAsset, getAssetActivities, getAssetList, getAssetStatusClearPeriods, getAssetStatuses } from "api/assets.api";
import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import { MAX_ROWS_FETCH, NUMBER_OF_ROWS_PER_PAGE } from "config/constants";
import { cloneDeep, min } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "stores/RootStore";
import { Asset, AssetActivity, AssetListResponse, AssetStatus, ClearPeriod } from "types/asset";
import { KeyValuePair } from "utils/common";

export class AssetListStore {
  root: RootStore;
  loading = true;
  error: ErrorResponseDto | null = null

  assetList: Asset[] = [];
  //this is for loading 200 records and FE paginating when the sort order in based on unpaginated sort fields
  catchedAssetList: Asset[] = [];
  selectedAsset: Asset | null = null
  totalCount: number = 0;
  nextToken: string = ""
  searchTerm: string = "";
  searchCategory: string = "";
  searchCategoriesList: KeyValuePair[] = [
    { value: 'name_filter', key: 'Asset Name' },
    { value: 'asset_type_filter', key: 'Asset Type' },
    { value: 'location_name_filter', key: 'Location' },
    { value: 'is_outdoors', key: 'Location' },
    { value: 'owner_filter', key: 'Owner' },
  ]
  sortField: string = "last_update"
  sortDirection: "asc" | "desc" = "desc"

  assetStatuses: AssetStatus[] = []
  clearPeriods: ClearPeriod[] = []
  assetActivities: Record<string, AssetActivity[]> = {}


  constructor(root: RootStore) {
    this.root = root;
    makeObservable(this, {
      loading: observable,
      error: observable,
      totalCount: observable,
      assetList: observable,
      selectedAsset: observable,
      searchTerm: observable,
      searchCategory: observable,
      searchCategoriesList: observable,
      sortField: observable,
      sortDirection: observable,
      assetActivities: observable,

      loadBaseData: action.bound,
      loadAssets: action.bound,
      setSelectedAsset: action.bound,
      setSearchTerm: action.bound,
      setSearchCategory: action.bound,
      reset: action.bound,
      onSort: action.bound,
      loadAssetActivities: action.bound,
      ArchiveAsset: action.bound,

      nextCount: computed,
      getSearchCategoryTitle: computed,
      getEmptyListMsg: computed
    });
  }

  reset() {
    this.searchTerm = ""
    this.searchCategory = ''
    this.assetList = []
    this.nextToken = ''
    this.loading = true
    this.error = null
    this.assetActivities = {}
    this.selectedAsset = null
  }

  setSearchTerm(search: string) {
    this.searchTerm = search
  }
  setSearchCategory(cat: string) {
    this.searchCategory = cat
  }

  setSelectedAsset(asset: Asset | null) {
    this.selectedAsset = asset
  }

  get getSearchCategoryTitle() {
    return this.searchCategoriesList.find(r => r.value === this.searchCategory)?.key || ""
  }

  frontendPagination() {
    return this.sortField === "last_update"
  }

  getLoadParams(firstPage: boolean) {
    const params: Record<string, any> = {
      limit: this.frontendPagination() ? MAX_ROWS_FETCH : NUMBER_OF_ROWS_PER_PAGE
    }
    if (this.searchTerm !== "") {
      if (this.searchCategory !== "") {
        if (this.searchCategory === 'is_outdoors')
          params.outdoors_filter = 'true'
        else
          params[this.searchCategory] = this.searchTerm;
      }
      else {
        this.searchCategoriesList.forEach(c => {
          if (c.value !== "is_outdoors")
            params[c.value] = this.searchTerm
        })
      }
    }
    if (this.sortField !== "") {
      params.sort = `${this.sortDirection === "desc" ? '-' : ''}${this.sortField}`
    }
    if (!firstPage && this.nextToken !== "")
      params.next = this.nextToken

    return params
  }

  loadBaseData() {
    Promise.all([
      getAssetStatuses(),
      getAssetStatusClearPeriods(),
    ]).then(res => {
      this.assetStatuses = res[0]
      this.clearPeriods = res[1]
    })
  }

  updateSelectedAsset() {
    if (!this.selectedAsset)
      return
    this.selectedAsset = cloneDeep(this.assetList.find(r => r.asset_id === this.selectedAsset?.asset_id) || null)
  }

  loadAssets(firstPage = false) {
    this.loading = true
    this.error = null

    if (!firstPage && this.frontendPagination()) {
      const pageNumber = this.assetList.length / NUMBER_OF_ROWS_PER_PAGE
      const start = pageNumber * NUMBER_OF_ROWS_PER_PAGE
      const end = ((pageNumber + 1) * NUMBER_OF_ROWS_PER_PAGE)
      this.assetList = [...this.assetList, ...this.catchedAssetList.slice(start, end)]
      this.loading = false
      return Promise.resolve()
    }

    return getAssetList(this.getLoadParams(firstPage))
      .then((res: AssetListResponse) => {
        if (firstPage) {
          if (this.frontendPagination()) {
            this.assetList = [...res.items.slice(0, NUMBER_OF_ROWS_PER_PAGE)]
            this.catchedAssetList = res.items
            this.totalCount = res.items.length
          }
          else {
            this.assetList = [...res.items]
            this.totalCount = res.total
          }
        }
        else {
          this.assetList = [...this.assetList, ...res.items]
          this.totalCount = res.total
        }
        this.nextToken = res.next
        this.updateSelectedAsset()
      })
      .catch((err: ErrorResponseDto) => this.error = err)
      .finally(() => this.loading = false)
  }

  loadAssetActivities(asset_id: string) {
    return getAssetActivities(asset_id)
      .then(res => this.assetActivities[asset_id] = res.items)
  }

  ArchiveAsset(asset_id: string) {
    return ArchiveAsset(asset_id)
      .then(() => this.loadAssets(true))
  }

  onSort(field: string, direction?: string) {
    if (direction) {
      this.sortDirection = direction as any
    }
    else {
      this.sortDirection = this.sortField !== field
        ? "asc"
        : this.sortDirection === "asc" ? "desc" : "asc"
    }
    this.sortField = field
    this.loadAssets(true)
  }

  get nextCount() {
    return min([this.totalCount - this.assetList.length, NUMBER_OF_ROWS_PER_PAGE]) || 0
  }

  get getEmptyListMsg() {
    if (this.searchTerm === "")
      return "No Assets to display"
    else
      return `Sorry, no results were found for ${this.searchTerm}
        ${this.searchCategory !== ""
          ? ` in <b>${this.searchCategoriesList.find(c => c.value === this.searchCategory)?.key}</b>`
          : ''
        }`
  }

}
